import Papa from "papaparse";
import { saveAs } from "file-saver";
import { v4 as uuidv4 } from "uuid";

/**
 * The funds load template and the checks run on an uploaded one.
 *
 * The file carries no company column. The company is chosen once in the wizard
 * and applied to every row, which is the same shape as the employee enrolment
 * upload. It also means a file cannot quietly move money into a company the
 * operator was not looking at.
 */

export const FUNDS_LOAD_PRODUCTS = ["HSA", "WSA", "Flex"];

/**
 * What a row does to the member's balance. A credit adds money to one bucket
 * and is the only action the company is billed for. A debit takes money out of
 * one bucket. A move takes money out of one bucket and puts it in another on
 * the same account, so the member's total does not change.
 */
export type FundsLoadAction = "credit" | "debit" | "move";
export type FundsLoadBucket = "available" | "carryover" | "grace";

export const FUNDS_LOAD_ACTIONS: { value: FundsLoadAction; label: string }[] = [
  { value: "credit", label: "Credit" },
  { value: "debit", label: "Debit" },
  { value: "move", label: "Move" },
];

// Carryover is carryover year 1. Renewal never fills year 2, so the service
// does not offer it.
export const FUNDS_LOAD_BUCKETS: { value: FundsLoadBucket; label: string }[] = [
  { value: "available", label: "Available" },
  { value: "carryover", label: "Carryover" },
  { value: "grace", label: "Grace" },
];

// Narrow a dropdown value to a known action or bucket by looking it up rather
// than casting. The production build's Babel does not accept "as" casts.
export const toFundsLoadAction = (value: unknown): FundsLoadAction =>
  FUNDS_LOAD_ACTIONS.find((known) => known.value === value)?.value ||
  "credit";

export const toFundsLoadBucket = (value: unknown): FundsLoadBucket | "" =>
  FUNDS_LOAD_BUCKETS.find((known) => known.value === value)?.value || "";

export const bucketLabel = (bucket?: string): string =>
  FUNDS_LOAD_BUCKETS.find((known) => known.value === bucket)?.label ||
  bucket ||
  "";

/**
 * One line naming what a row does, for the review and results tables and the
 * confirmation, so the operator reads the same words everywhere.
 */
export const describeFundsChange = (
  action: string,
  fromBucket?: string,
  toBucket?: string
): string => {
  switch (action) {
    case "debit":
      return `Debit from ${bucketLabel(fromBucket)}`;
    case "move":
      return `Move from ${bucketLabel(fromBucket)} to ${bucketLabel(toBucket)}`;
    default:
      return `Credit to ${bucketLabel(toBucket || "available")}`;
  }
};

/**
 * Checks that the buckets fit the action and fills the defaults, the same way
 * the service does. A credit names only the destination and defaults to
 * Available; a debit names only the source and defaults to Available; a move
 * names both, and they must differ. A bucket on the wrong side is an error
 * rather than ignored: "Debit ... to Grace" means the operator had something
 * else in mind, and guessing would move the wrong money.
 */
export const resolveBuckets = (
  action: FundsLoadAction,
  fromBucket: FundsLoadBucket | "",
  toBucket: FundsLoadBucket | ""
): {
  fromBucket: FundsLoadBucket | "";
  toBucket: FundsLoadBucket | "";
  error: string | null;
} => {
  if (action === "credit") {
    if (fromBucket) {
      return {
        fromBucket,
        toBucket,
        error: "a credit only takes a To Bucket - leave From Bucket empty",
      };
    }
    return { fromBucket: "", toBucket: toBucket || "available", error: null };
  }
  if (action === "debit") {
    if (toBucket) {
      return {
        fromBucket,
        toBucket,
        error: "a debit only takes a From Bucket - leave To Bucket empty",
      };
    }
    return {
      fromBucket: fromBucket || "available",
      toBucket: "",
      error: null,
    };
  }
  if (!fromBucket || !toBucket) {
    return {
      fromBucket,
      toBucket,
      error: "a move needs both a From Bucket and a To Bucket",
    };
  }
  if (fromBucket === toBucket) {
    return {
      fromBucket,
      toBucket,
      error: "a move needs two different buckets",
    };
  }
  return { fromBucket, toBucket, error: null };
};

// The header row of the template, in order. Parsing is done by header name
// rather than by position, so an operator who reorders or adds columns is not
// punished for it, but the download keeps this order because it reads well.
export const FUNDS_LOAD_COLUMNS = [
  "Employee Email",
  "Person GUID",
  "Product",
  "Action",
  "From Bucket",
  "To Bucket",
  "Amount",
  "Reason",
];

// Columns a file may leave out. A file from before actions existed has no
// Action or bucket columns, and still loads exactly as it did: a credit to
// Available.
const OPTIONAL_COLUMNS = ["Person GUID", "Action", "From Bucket", "To Bucket"];

export interface FundsLoadRow {
  rowNumber: number;
  email: string;
  personGuid: string;
  product: string;
  action: FundsLoadAction;
  fromBucket: FundsLoadBucket | "";
  toBucket: FundsLoadBucket | "";
  amount: number;
  reason: string;
  idempotencyKey: string;
}

export interface FundsLoadParseResult {
  rows: FundsLoadRow[];
  errors: string[];
}

/**
 * Downloads the blank template, with one example row per action so the
 * expected format of each column is visible rather than guessed at.
 */
export const downloadFundsLoadTemplate = (): void => {
  const csv = Papa.unparse({
    fields: FUNDS_LOAD_COLUMNS,
    data: [
      [
        "employee@company.com",
        "",
        "HSA",
        "Credit",
        "",
        "Available",
        "250.00",
        "Missed January contribution",
      ],
      [
        "employee@company.com",
        "",
        "HSA",
        "Debit",
        "Grace",
        "",
        "40.00",
        "Grace loaded twice",
      ],
      [
        "employee@company.com",
        "",
        "HSA",
        "Move",
        "Available",
        "Carryover",
        "100.00",
        "Carryover missed at renewal",
      ],
    ],
  });
  saveAs(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    "Funds Load Template.csv"
  );
};

const readCell = (row: any, column: string): string =>
  String(row?.[column] ?? "").trim();

const matchAction = (value: string): FundsLoadAction | null => {
  if (value === "") {
    return "credit";
  }
  return (
    FUNDS_LOAD_ACTIONS.find((known) => known.value === value.toLowerCase())
      ?.value || null
  );
};

const matchBucket = (value: string): FundsLoadBucket | "" | null => {
  if (value === "") {
    return "";
  }
  return (
    FUNDS_LOAD_BUCKETS.find((known) => known.value === value.toLowerCase())
      ?.value || null
  );
};

/**
 * Turns an amount as typed into a number.
 *
 * Operators paste from spreadsheets, so "$1,250.00" is normal input. Anything
 * that is not a positive number after that is rejected rather than coerced:
 * silently reading "12.OO" as 12 would move the wrong amount of money. A debit
 * is a positive amount with the Debit action, not a negative amount, so a stray
 * minus sign is still an error.
 */
const parseAmount = (value: string): number | null => {
  const cleaned = value.replace(/[$,\s]/g, "");
  if (cleaned === "") {
    return null;
  }
  const amount = Number(cleaned);
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  // More than two decimals is not a cent amount and is very likely a typo.
  if (Math.round(amount * 100) / 100 !== amount) {
    return null;
  }
  return amount;
};

/**
 * Parses and checks an uploaded template.
 *
 * Every problem found is reported, not just the first, so an operator can fix
 * a file in one pass instead of re-uploading once per bad row. A file with any
 * error at all is refused outright: a half-loaded file is the outcome hardest
 * to recover from, and the checks here are cheap compared with reversing a
 * ledger entry.
 *
 * @param {*} parsed rows from papaparse, parsed with header: true
 * @param {string} batchId groups the idempotency keys of one submission
 */
export const validateFundsLoadRows = (
  parsed: any[],
  batchId: string
): FundsLoadParseResult => {
  const errors: string[] = [];
  const rows: FundsLoadRow[] = [];

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return { rows: [], errors: ["The file has no rows."] };
  }

  const headers = Object.keys(parsed[0] || {});
  const missingColumns = FUNDS_LOAD_COLUMNS.filter(
    (column) => !OPTIONAL_COLUMNS.includes(column) && !headers.includes(column)
  );
  if (missingColumns.length > 0) {
    return {
      rows: [],
      errors: [
        `The file is missing these columns: ${missingColumns.join(
          ", "
        )}. Download the template and fill that in.`,
      ],
    };
  }

  // The exact same change twice for one member is almost always a copy-paste
  // slip, and it would apply twice. Different actions on the same product are
  // fine: a move followed by a credit is a normal correction.
  const seen = new Set<string>();

  parsed.forEach((raw, index) => {
    // Row 1 is the header the operator sees in their spreadsheet, so the data
    // starts at row 2. Reporting the spreadsheet's own numbering is what makes
    // an error message actionable.
    const rowNumber = index + 2;

    const email = readCell(raw, "Employee Email");
    const personGuid = readCell(raw, "Person GUID");
    const product = readCell(raw, "Product");
    const actionRaw = readCell(raw, "Action");
    const fromBucketRaw = readCell(raw, "From Bucket");
    const toBucketRaw = readCell(raw, "To Bucket");
    const amountRaw = readCell(raw, "Amount");
    const reason = readCell(raw, "Reason");

    // Papaparse yields a trailing blank row for a file ending in a newline.
    if (
      !email &&
      !personGuid &&
      !product &&
      !actionRaw &&
      !fromBucketRaw &&
      !toBucketRaw &&
      !amountRaw &&
      !reason
    ) {
      return;
    }

    if (!email && !personGuid) {
      errors.push(
        `Row ${rowNumber}: an employee email or person GUID is required.`
      );
    }

    const matchedProduct = FUNDS_LOAD_PRODUCTS.find(
      (known) => known.toLowerCase() === product.toLowerCase()
    );
    if (!matchedProduct) {
      errors.push(
        `Row ${rowNumber}: product must be one of ${FUNDS_LOAD_PRODUCTS.join(
          ", "
        )}.`
      );
    }

    const action = matchAction(actionRaw);
    if (action === null) {
      errors.push(
        `Row ${rowNumber}: action must be one of ${FUNDS_LOAD_ACTIONS.map(
          (known) => known.label
        ).join(", ")}.`
      );
    }

    const bucketNames = FUNDS_LOAD_BUCKETS.map((known) => known.label).join(
      ", "
    );
    const fromBucketMatch = matchBucket(fromBucketRaw);
    if (fromBucketMatch === null) {
      errors.push(
        `Row ${rowNumber}: From Bucket must be one of ${bucketNames}, or empty.`
      );
    }
    const toBucketMatch = matchBucket(toBucketRaw);
    if (toBucketMatch === null) {
      errors.push(
        `Row ${rowNumber}: To Bucket must be one of ${bucketNames}, or empty.`
      );
    }

    let buckets: ReturnType<typeof resolveBuckets> | null = null;
    if (action !== null && fromBucketMatch !== null && toBucketMatch !== null) {
      buckets = resolveBuckets(action, fromBucketMatch, toBucketMatch);
      if (buckets.error) {
        errors.push(`Row ${rowNumber}: ${buckets.error}.`);
      }
    }

    const amount = parseAmount(amountRaw);
    if (amount === null) {
      errors.push(
        `Row ${rowNumber}: amount must be a positive number with at most two decimals.`
      );
    }

    if (!reason) {
      errors.push(`Row ${rowNumber}: a reason is required.`);
    } else if (reason.length > 200) {
      errors.push(`Row ${rowNumber}: reason must be 200 characters or fewer.`);
    }

    const key = [
      (personGuid || email).toLowerCase(),
      product.toLowerCase(),
      action,
      buckets?.fromBucket,
      buckets?.toBucket,
    ].join("|");
    if (seen.has(key)) {
      errors.push(
        `Row ${rowNumber}: this member already has the same ${product} ${
          action
            ? describeFundsChange(
                action,
                buckets?.fromBucket,
                buckets?.toBucket
              ).toLowerCase()
            : "change"
        } in this file.`
      );
    }
    seen.add(key);

    if (
      matchedProduct &&
      action !== null &&
      buckets &&
      !buckets.error &&
      amount !== null &&
      reason &&
      reason.length <= 200 &&
      (email || personGuid)
    ) {
      rows.push({
        rowNumber,
        email,
        personGuid,
        product: matchedProduct,
        action,
        fromBucket: buckets.fromBucket,
        toBucket: buckets.toBucket,
        amount,
        reason,
        // Stable for this batch and row, so re-submitting the same parsed file
        // cannot apply anything twice.
        idempotencyKey: `${batchId}-row-${rowNumber}`,
      });
    }
  });

  if (rows.length === 0 && errors.length === 0) {
    errors.push("The file has no rows to load.");
  }

  return { rows, errors };
};

/**
 * Totals per action, for the review and confirmation banners. Only credits are
 * billed to the company, so they are never added together with the others.
 */
export const summarizeFundsChanges = (
  rows: { action: string; amount: number }[]
): Record<FundsLoadAction, { count: number; total: number }> => {
  const summary: Record<FundsLoadAction, { count: number; total: number }> = {
    credit: { count: 0, total: 0 },
    debit: { count: 0, total: 0 },
    move: { count: 0, total: 0 },
  };
  rows.forEach((row) => {
    const entry = summary[toFundsLoadAction(row.action)];
    entry.count += 1;
    // Summed in cents so a long file does not drift by floating point.
    entry.total = Math.round(entry.total * 100 + row.amount * 100) / 100;
  });
  return summary;
};

export const newFundsLoadBatchId = (): string => uuidv4();

export const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(Number.isFinite(amount) ? amount : 0);
